import { useLocale } from '@repo/i18n';
import { BilberryPromoCodeStatus, CartItem, OctoOrder } from '@repo/types';
import { currencyAtom } from '@repo/widget-utils/currencyAtom';
import { useMemo } from 'react';
import {
    getSummaryCartItemsByTypeFromCartItems,
    getSummaryCartItemsByTypeFromOctoOrder,
} from 'src/components/domain/summary/summary-mapping';
import { paymentPlanAtom } from 'src/state/payment-plan/paymentPlanAtom';
import { useAtom } from 'ximple';
import useActiveOctoBookings from './useActiveOctoBookings';
import { useConfigurations, useMembershipMode } from '@repo/widget-utils/widgetsConfiguration';
import { createInitialCheckoutInfoData } from '@repo/widget-utils/checkout-info-helper';
import { nativeMemberCoverageAtom } from 'src/state/native-membership/nativeMemberCoverageAtom';

export default function useSummaryCartItemsByType(
    cartItems: CartItem[],
    isLoadingOctoOrder: boolean,
    octoOrder: OctoOrder | undefined,
    appliedPromoCode: BilberryPromoCodeStatus | null,
) {
    const { t, locale } = useLocale();
    const configurations = useConfigurations();
    const [currency] = useAtom(currencyAtom);
    const [{ isUsingPaymentPlan, isLoadingPrices }] = useAtom(paymentPlanAtom);
    const activeOctoBookings = useActiveOctoBookings(octoOrder);
    const isNative = useMembershipMode() === 'native';
    const [
        {
            result: nativeResultLive,
            committedResult: nativeCommitted,
            isCalculating: nativeCalculating,
        },
    ] = useAtom(nativeMemberCoverageAtom);
    const nativeResult = isNative ? (nativeResultLive ?? nativeCommitted) : null;
    // Until /calculate resolves we don't know the member's price — show the existing loading UX
    // (skeleton lines) instead of flashing the gross price. Mirrors legacy payment-plan isLoadingPrices.
    const nativePricesLoading = isNative && nativeCalculating;

    return useMemo(() => {
        if (configurations.octo) {
            return getSummaryCartItemsByTypeFromOctoOrder(
                isLoadingOctoOrder,
                octoOrder,
                activeOctoBookings,
                locale,
                t,
            );
        } else {
            // Native membership: pass the coverage result + a placeholder checkoutInfo so the per-line
            // mapper can show member-card coverage (discounted / "Gratis" / "N klipp"). Null otherwise.
            const nativeCheckoutInfo = nativeResult
                ? createInitialCheckoutInfoData(null, cartItems, null)
                : null;
            return getSummaryCartItemsByTypeFromCartItems(
                cartItems,
                appliedPromoCode,
                locale,
                currency,
                t,
                isLoadingPrices || nativePricesLoading,
                isUsingPaymentPlan,
                nativeResult,
                nativeCheckoutInfo,
            );
        }
    }, [
        configurations.octo,
        isLoadingOctoOrder,
        octoOrder,
        activeOctoBookings,
        cartItems,
        appliedPromoCode,
        locale,
        currency,
        t,
        isLoadingPrices,
        isUsingPaymentPlan,
        nativeResult,
        nativePricesLoading,
    ]);
}
